<template>
  <v-container fluid>
    <v-alert
      v-model="alert"
      class="mb-4"
      closable
      :text="alertText"
      :type="alertType"
    />

    <template v-if="isProfessor && page === 'Professor'">
      <v-card
        prepend-icon="mdi-text-box-edit"
        title="Avaliação Discursiva - Professor"
      >
        <v-card-text>
          <v-row dense>
            <v-col cols="12" md="6"
              ><v-text-field v-model="form.titulo" label="Título interno"
            /></v-col>
            <v-col cols="12" md="6"
              ><v-text-field
                v-model="form.area_conhecimento"
                label="Área de conhecimento"
            /></v-col>
            <v-col cols="12" md="3"
              ><v-select
                v-model="form.avaliacao"
                label="Avaliação"
                :items="['A1', 'A2', 'A3(A1)', 'A3(A2)']"
            /></v-col>
            <v-col cols="12" md="3"
              ><v-select
                v-model="form.bimestre"
                label="Bimestre"
                :items="['1B', '2B']"
            /></v-col>
            <v-col cols="12" md="3">
              <v-select
                v-model="form.turma"
                label="Turma"
                :items="turmas"
                item-title="display"
                item-value="codigo"
                :loading="loadingTurmas"
                no-data-text="Nenhuma turma cadastrada"
                @update:modelValue="handleTurmaChange"
              />
            </v-col>
            <v-col cols="12" md="3"
              ><v-text-field
                v-model="form.periodo_letivo"
                label="Período letivo"
            /></v-col>
            <v-col cols="12" md="3"
              ><v-text-field
                v-model="form.data_avaliacao"
                label="Data"
                placeholder="__/__/2026"
            /></v-col>
            <v-col cols="12" md="3"
              ><v-text-field v-model="form.valor_total" label="Valor total"
            /></v-col>
          </v-row>

          <v-expansion-panels variant="accordion" class="mt-4">
            <v-expansion-panel
              v-for="(questao, index) in form.questoes"
              :key="index"
            >
              <v-expansion-panel-title
                >Questão {{ index + 1 }} -
                {{ questao.taxonomia }}</v-expansion-panel-title
              >
              <v-expansion-panel-text>
                <v-row dense>
                  <v-col cols="12" md="2"
                    ><v-text-field v-model="questao.valor" label="Valor"
                  /></v-col>
                  <v-col cols="12" md="5"
                    ><v-text-field
                      v-model="questao.area"
                      label="Área da questão"
                  /></v-col>
                  <v-col cols="12" md="3"
                    ><v-select
                      v-model="questao.taxonomia"
                      label="Taxonomia"
                      :items="['Baixa', 'Média', 'Alta']"
                      @update:modelValue="applyTaxonomyLines(index)"
                  /></v-col>
                  <v-col cols="12" md="2" class="d-flex align-center ga-2">
                    <v-btn
                      icon="mdi-minus"
                      size="small"
                      variant="tonal"
                      @click="adjustQuestionLines(index, -1)"
                    />
                    <v-text-field
                      v-model.number="questao.linhas"
                      type="number"
                      min="1"
                      label="Linhas"
                      hide-details
                    />
                    <v-btn
                      icon="mdi-plus"
                      size="small"
                      variant="tonal"
                      @click="adjustQuestionLines(index, 1)"
                    />
                  </v-col>
                  <v-col cols="12"
                    ><v-textarea
                      v-model="questao.enunciado"
                      auto-grow
                      label="Questão completa"
                  /></v-col>
                  <v-col cols="12">
                    <v-file-input
                      accept="image/png,image/jpeg"
                      label="Figura da questão (opcional)"
                      prepend-icon="mdi-image"
                      show-size
                      @change="handleQuestionImage($event, index)"
                    />
                    <img
                      v-if="questao.image_data_url"
                      :src="questao.image_data_url"
                      class="discursive-question-image mt-3"
                      alt="Figura da questão"
                    />
                    <v-btn
                      v-if="questao.image_data_url"
                      class="mt-2"
                      color="error"
                      variant="text"
                      @click="removeQuestionImage(index)"
                    >
                      Remover figura
                    </v-btn>
                  </v-col>
                  <v-col cols="12" md="6"
                    ><v-textarea
                      v-model="questao.gabarito"
                      auto-grow
                      label="Gabarito esperado"
                  /></v-col>
                  <v-col cols="12" md="6"
                    ><v-textarea
                      v-model="questao.referencia"
                      auto-grow
                      label="Referência"
                  /></v-col>
                </v-row>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-card-text>
        <v-card-actions>
          <v-btn
            color="primary"
            variant="flat"
            :loading="loading"
            @click="createEvaluation"
            >Gerar avaliação</v-btn
          >
          <v-btn variant="outlined" @click="resetForm">Limpar</v-btn>
        </v-card-actions>
      </v-card>
    </template>

    <template v-else-if="isProfessor && page === 'ConsultarProtocolo'">
      <v-card prepend-icon="mdi-magnify" title="Consultar protocolo ou código">
        <v-card-text>
          <v-row dense>
            <v-col cols="12" md="9">
              <v-text-field
                v-model="protocolQuery"
                label="Código da avaliação ou protocolo"
                clearable
              />
            </v-col>
            <v-col cols="12" md="3" class="d-flex align-center">
              <v-btn color="primary" block @click="findProtocol"
                >Consultar</v-btn
              >
            </v-col>
          </v-row>
          <v-alert
            v-if="!selectedEvaluation"
            type="info"
            variant="tonal"
            class="mt-2"
          >
            Informe o código da avaliação ou o protocolo para ver a prova e as
            respostas dos alunos.
          </v-alert>
          <v-card v-if="selectedEvaluation" variant="tonal" class="mt-3">
            <v-card-text>
              <v-row dense>
                <v-col cols="12" md="6"
                  ><strong>Título:</strong>
                  {{ selectedEvaluation.titulo }}</v-col
                >
                <v-col cols="12" md="6"
                  ><strong>Status:</strong>
                  {{ statusLabel(selectedEvaluation.status) }}</v-col
                >
                <v-col cols="12" md="6"
                  ><strong>Código para alunos:</strong>
                  {{ selectedEvaluation.access_code }}</v-col
                >
                <v-col cols="12" md="6"
                  ><strong>Protocolo:</strong>
                  {{ selectedEvaluation.id }}</v-col
                >
                <v-col cols="12" md="6"
                  ><strong>Turma:</strong> {{ selectedEvaluation.turma }}</v-col
                >
                <v-col cols="12" md="6"
                  ><strong>Respostas recebidas:</strong>
                  {{ selectedEvaluationAnswers.length }}</v-col
                >
              </v-row>
            </v-card-text>
          </v-card>
          <v-card
            v-if="selectedEvaluation"
            class="mt-4"
            prepend-icon="mdi-account-check"
            title="Alunos que fizeram a prova"
          >
            <v-card-text>
              <v-alert
                v-if="!selectedEvaluationAnswers.length"
                type="info"
                variant="tonal"
                class="mb-4"
              >
                Nenhum aluno respondeu esta avaliação ainda.
              </v-alert>
              <v-list
                v-if="selectedEvaluationAnswers.length"
                lines="three"
                class="discursive-list"
              >
                <v-list-item
                  v-for="answer in selectedEvaluationAnswers"
                  :key="answer.id"
                >
                  <template #title>
                    <strong>{{ answer.nome_aluno }}</strong>
                  </template>
                  <template #subtitle>
                    <div class="d-flex flex-wrap ga-2 mt-2">
                      <v-chip size="small" color="primary" variant="tonal"
                        >Código: {{ answer.avaliacao_codigo }}</v-chip
                      >
                      <v-chip size="small" variant="tonal"
                        >Resposta: {{ answer.id }}</v-chip
                      >
                      <v-chip size="small" variant="tonal">{{
                        answer.email_aluno
                      }}</v-chip>
                      <v-chip size="small" variant="tonal"
                        >RA: {{ answer.aluno_ra || answer.aluno_ra_atual || "-" }}</v-chip
                      >
                      <v-chip size="small" variant="tonal">{{
                        answer.status
                      }}</v-chip>
                      <v-chip
                        v-if="
                          answer.nota_total !== null &&
                          answer.nota_total !== undefined
                        "
                        size="small"
                        color="success"
                        variant="tonal"
                        >Nota: {{ formatGrade(answer.nota_total) }}</v-chip
                      >
                    </div>
                    <div class="text-caption mt-1">
                      Enviada em: {{ formatDate(answer.datetime) }}
                    </div>
                  </template>
                  <template #append>
                    <v-btn
                      size="small"
                      color="primary"
                      variant="flat"
                      @click="selectAnswerItem(answer)"
                      >Corrigir</v-btn
                    >
                  </template>
                </v-list-item>
              </v-list>
              <div v-if="selectedAnswer" class="mt-4">
                <h3>Prova do aluno para correção</h3>
                <v-row dense class="mb-2">
                  <v-col cols="12" md="6"
                    ><strong>Aluno:</strong>
                    {{ selectedAnswer.nome_aluno }}</v-col
                  >
                  <v-col cols="12" md="6"
                    ><strong>E-mail:</strong>
                    {{ selectedAnswer.email_aluno }}</v-col
                  >
                  <v-col cols="12" md="6"
                    ><strong>RA:</strong>
                    {{ selectedAnswer.aluno_ra || selectedAnswer.aluno_ra_atual || "-" }}</v-col
                  >
                  <v-col cols="12" md="6"
                    ><strong>Protocolo da resposta:</strong>
                    {{ selectedAnswer.id }}</v-col
                  >
                  <v-col cols="12" md="6"
                    ><strong>Enviada em:</strong>
                    {{ formatDate(selectedAnswer.datetime) }}</v-col
                  >
                  <v-col cols="12" md="6"
                    ><strong>Status:</strong> {{ selectedAnswer.status }}</v-col
                  >
                  <v-col cols="12" md="6"
                    ><strong>Nota total:</strong>
                    {{ correctionTotalLabel(selectedAnswer) }} /
                    {{ selectedEvaluation.valor_total }}</v-col
                  >
                </v-row>
                <v-expansion-panels>
                  <v-expansion-panel
                    v-for="(questao, index) in selectedAnswer.questoes || []"
                    :key="index"
                  >
                    <v-expansion-panel-title
                      >Questão {{ index + 1 }} -
                      {{ questao.taxonomia }}</v-expansion-panel-title
                    >
                    <v-expansion-panel-text>
                      <p><strong>Enunciado</strong></p>
                      <p style="white-space: pre-wrap">
                        {{ questao.enunciado }}
                      </p>
                      <img
                        v-if="questao.image_data_url"
                        :src="questao.image_data_url"
                        class="discursive-question-image my-3"
                        alt="Figura da questão"
                      />
                      <v-row>
                        <v-col cols="12" md="6"
                          ><strong>Resposta do aluno</strong>
                          <p style="white-space: pre-wrap">
                            {{ answerText(index) }}
                          </p></v-col
                        >
                        <v-col cols="12" md="6"
                          ><strong>Gabarito esperado</strong>
                          <p style="white-space: pre-wrap">
                            {{ questao.gabarito }}
                          </p></v-col
                        >
                        <v-col cols="12" md="4">
                          <v-text-field
                            v-model="selectedAnswer.notas[index].nota"
                            :label="`Nota da questão (valor ${questionValue(
                              questao
                            )})`"
                            type="text"
                            density="compact"
                          />
                        </v-col>
                      </v-row>
                    </v-expansion-panel-text>
                  </v-expansion-panel>
                </v-expansion-panels>
                <v-textarea
                  v-model="selectedAnswer.observacao_admin"
                  auto-grow
                  label="Observação da correção"
                  class="mt-4"
                />
                <v-row dense class="align-center mt-2">
                  <v-col cols="12" md="6"
                    ><strong>Nota total:</strong>
                    {{ correctionTotalLabel(selectedAnswer) }} /
                    {{ selectedEvaluation.valor_total }}</v-col
                  >
                  <v-col cols="12" md="6" class="text-md-right">
                    <v-btn
                      color="success"
                      variant="flat"
                      :loading="loading"
                      @click="saveCorrection"
                      >Salvar correção</v-btn
                    >
                  </v-col>
                </v-row>
              </div>
            </v-card-text>
          </v-card>
          <v-card
            v-if="selectedEvaluation"
            class="mt-4"
            prepend-icon="mdi-file-eye"
            title="Prova confeccionada"
          >
            <v-card-text>
              <v-expansion-panels>
                <v-expansion-panel
                  v-for="(questao, index) in selectedEvaluation.questoes || []"
                  :key="index"
                >
                  <v-expansion-panel-title
                    >Questão {{ index + 1 }} - {{ questao.taxonomia }} |
                    {{ questionLines(questao) }} linhas</v-expansion-panel-title
                  >
                  <v-expansion-panel-text>
                    <p><strong>Enunciado</strong></p>
                    <p style="white-space: pre-wrap">{{ questao.enunciado }}</p>
                    <img
                      v-if="questao.image_data_url"
                      :src="questao.image_data_url"
                      class="discursive-question-image my-3"
                      alt="Figura da questão"
                    />
                    <v-row>
                      <v-col cols="12" md="6"
                        ><strong>Gabarito esperado</strong>
                        <p style="white-space: pre-wrap">
                          {{ questao.gabarito }}
                        </p></v-col
                      >
                      <v-col cols="12" md="6"
                        ><strong>Referência</strong>
                        <p style="white-space: pre-wrap">
                          {{ questao.referencia }}
                        </p></v-col
                      >
                    </v-row>
                  </v-expansion-panel-text>
                </v-expansion-panel>
              </v-expansion-panels>
            </v-card-text>
          </v-card>
        </v-card-text>
      </v-card>
    </template>

    <template v-else-if="isProfessor && page === 'Admin'">
      <v-row>
        <v-col cols="12" md="5">
          <v-card
            prepend-icon="mdi-clipboard-list"
            title="Avaliações discursivas"
          >
            <v-card-text>
              <v-row dense>
                <v-col cols="12" md="7">
                  <v-text-field
                    v-model="evaluationSearch"
                    label="Buscar por título, código, turma ou protocolo"
                    clearable
                  />
                </v-col>
                <v-col cols="12" md="5">
                  <v-select
                    v-model="adminStatusFilter"
                    label="Status"
                    :items="adminStatusOptions"
                    item-title="label"
                    item-value="value"
                  />
                </v-col>
              </v-row>
              <v-alert
                v-if="!filteredEvaluations.length"
                type="info"
                variant="tonal"
                class="mb-3"
              >
                Nenhuma avaliação encontrada para os filtros selecionados.
              </v-alert>
              <v-list lines="three" class="discursive-list">
                <v-list-item v-for="item in filteredEvaluations" :key="item.id">
                  <template #title>
                    <strong>{{ item.titulo }}</strong>
                  </template>
                  <template #subtitle>
                    <div class="d-flex flex-wrap ga-2 mt-2">
                      <v-chip size="small" color="primary" variant="tonal"
                        >Código: {{ item.access_code }}</v-chip
                      >
                      <v-chip size="small" variant="tonal"
                        >Protocolo: {{ item.id }}</v-chip
                      >
                      <v-chip
                        size="small"
                        :color="statusColor(item.status)"
                        variant="tonal"
                        >{{ statusLabel(item.status) }}</v-chip
                      >
                      <v-chip size="small" variant="tonal"
                        >Respostas: {{ item.respostas_count || 0 }}</v-chip
                      >
                    </div>
                    <div class="text-caption mt-1">
                      Turma: {{ item.turma }} | Criada em:
                      {{ formatDate(item.datetime) }}
                    </div>
                  </template>
                  <template #append>
                    <v-btn
                      size="small"
                      color="primary"
                      variant="flat"
                      @click="selectEvaluationItem(item)"
                      >Abrir</v-btn
                    >
                  </template>
                </v-list-item>
              </v-list>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" md="7">
          <v-alert
            v-if="!selectedEvaluation"
            type="info"
            variant="tonal"
            class="mb-4"
          >
            Selecione uma avaliação para aprovar, solicitar correção ou liberar
            para os alunos.
          </v-alert>
          <v-card
            v-if="selectedEvaluation"
            prepend-icon="mdi-cog"
            title="Gestão da avaliação"
          >
            <v-card-text>
              <v-row dense>
                <v-col cols="12" md="6"
                  ><p>
                    <strong>Protocolo:</strong> {{ selectedEvaluation.id }}
                  </p></v-col
                >
                <v-col cols="12" md="6"
                  ><p>
                    <strong>Código para alunos:</strong>
                    {{ selectedEvaluation.access_code }}
                  </p></v-col
                >
                <v-col cols="12" md="6"
                  ><p>
                    <strong>Título:</strong> {{ selectedEvaluation.titulo }}
                  </p></v-col
                >
                <v-col cols="12" md="6"
                  ><p>
                    <strong>Turma:</strong> {{ selectedEvaluation.turma }}
                  </p></v-col
                >
                <v-col cols="12" md="6"
                  ><p>
                    <strong>Criada em:</strong>
                    {{ formatDate(selectedEvaluation.datetime) }}
                  </p></v-col
                >
                <v-col cols="12" md="6"
                  ><p>
                    <strong>Respostas recebidas:</strong>
                    {{ selectedEvaluationAnswers.length }}
                  </p></v-col
                >
              </v-row>
              <v-select
                v-model="selectedEvaluation.status"
                label="Status"
                :items="statusOptions"
                item-title="label"
                item-value="value"
              />
              <v-textarea
                v-model="selectedEvaluation.observacao_admin"
                auto-grow
                label="Observação para acompanhamento"
              />
              <v-btn
                color="primary"
                variant="flat"
                :loading="loading"
                @click="updateEvaluation"
                >Salvar status</v-btn
              >
              <v-divider class="my-4" />
              <h3>Prova construída</h3>
              <v-expansion-panels class="mt-3">
                <v-expansion-panel
                  v-for="(questao, index) in selectedEvaluation.questoes || []"
                  :key="index"
                >
                  <v-expansion-panel-title
                    >Questão {{ index + 1 }} - {{ questao.taxonomia }} |
                    {{ questionLines(questao) }} linhas</v-expansion-panel-title
                  >
                  <v-expansion-panel-text>
                    <p><strong>Enunciado</strong></p>
                    <p style="white-space: pre-wrap">{{ questao.enunciado }}</p>
                    <img
                      v-if="questao.image_data_url"
                      :src="questao.image_data_url"
                      class="discursive-question-image my-3"
                      alt="Figura da questão"
                    />
                    <v-row>
                      <v-col cols="12" md="6"
                        ><strong>Gabarito esperado</strong>
                        <p style="white-space: pre-wrap">
                          {{ questao.gabarito }}
                        </p></v-col
                      >
                      <v-col cols="12" md="6"
                        ><strong>Referência</strong>
                        <p style="white-space: pre-wrap">
                          {{ questao.referencia }}
                        </p></v-col
                      >
                    </v-row>
                  </v-expansion-panel-text>
                </v-expansion-panel>
              </v-expansion-panels>
            </v-card-text>
          </v-card>
          <v-card
            class="mt-4"
            prepend-icon="mdi-account-check"
            title="Respostas dos alunos"
          >
            <v-card-text>
              <v-alert
                v-if="selectedEvaluation && !selectedEvaluationAnswers.length"
                type="info"
                variant="tonal"
                class="mb-4"
              >
                Nenhum aluno respondeu esta avaliação ainda.
              </v-alert>
              <v-data-table
                :headers="answerHeaders"
                :items="visibleAnswers"
                :loading="loading"
              >
                <template v-slot:item.datetime="{ item }">{{
                  formatDate(rowValue(item).datetime)
                }}</template>
                <template v-slot:item.status="{ item }">
                  <v-chip size="small" variant="tonal">{{
                    rowValue(item).status
                  }}</v-chip>
                </template>
                <template v-slot:item.actions="{ item }">
                  <v-btn
                    size="small"
                    color="primary"
                    variant="text"
                    @click.stop="selectAnswerItem(item)"
                    >Comparar</v-btn
                  >
                </template>
              </v-data-table>
              <div v-if="selectedAnswer" class="mt-4">
                <h3>Correção comparativa</h3>
                <v-row dense class="mb-2">
                  <v-col cols="12" md="6"
                    ><strong>Aluno:</strong>
                    {{ selectedAnswer.nome_aluno }}</v-col
                  >
                  <v-col cols="12" md="6"
                    ><strong>E-mail:</strong>
                    {{ selectedAnswer.email_aluno }}</v-col
                  >
                  <v-col cols="12" md="6"
                    ><strong>Protocolo da resposta:</strong>
                    {{ selectedAnswer.id }}</v-col
                  >
                  <v-col cols="12" md="6"
                    ><strong>Enviada em:</strong>
                    {{ formatDate(selectedAnswer.datetime) }}</v-col
                  >
                  <v-col cols="12" md="6"
                    ><strong>Status:</strong> {{ selectedAnswer.status }}</v-col
                  >
                  <v-col cols="12" md="6"
                    ><strong>Nota total:</strong>
                    {{ correctionTotalLabel(selectedAnswer) }} /
                    {{ selectedEvaluation.valor_total }}</v-col
                  >
                </v-row>
                <v-expansion-panels>
                  <v-expansion-panel
                    v-for="(questao, index) in selectedAnswer.questoes"
                    :key="index"
                  >
                    <v-expansion-panel-title
                      >Questão {{ index + 1 }} - {{ questao.taxonomia }} |
                      {{ questionLines(questao) }}
                      linhas</v-expansion-panel-title
                    >
                    <v-expansion-panel-text>
                      <p><strong>Enunciado</strong></p>
                      <p style="white-space: pre-wrap">
                        {{ questao.enunciado }}
                      </p>
                      <img
                        v-if="questao.image_data_url"
                        :src="questao.image_data_url"
                        class="discursive-question-image my-3"
                        alt="Figura da questão"
                      />
                      <v-row>
                        <v-col cols="12" md="6"
                          ><strong>Resposta do aluno</strong>
                          <p style="white-space: pre-wrap">
                            {{ answerText(index) }}
                          </p></v-col
                        >
                        <v-col cols="12" md="6"
                          ><strong>Gabarito esperado</strong>
                          <p style="white-space: pre-wrap">
                            {{ questao.gabarito }}
                          </p></v-col
                        >
                        <v-col cols="12" md="4">
                          <v-text-field
                            v-model="selectedAnswer.notas[index].nota"
                            :label="`Nota da questão (valor ${questionValue(
                              questao
                            )})`"
                            type="text"
                            density="compact"
                          />
                        </v-col>
                      </v-row>
                    </v-expansion-panel-text>
                  </v-expansion-panel>
                </v-expansion-panels>
                <v-textarea
                  v-model="selectedAnswer.observacao_admin"
                  auto-grow
                  label="Observação da correção"
                  class="mt-4"
                />
                <v-row dense class="align-center mt-2">
                  <v-col cols="12" md="6"
                    ><strong>Nota total:</strong>
                    {{ correctionTotalLabel(selectedAnswer) }} /
                    {{ selectedEvaluation.valor_total }}</v-col
                  >
                  <v-col cols="12" md="6" class="text-md-right">
                    <v-btn
                      color="success"
                      variant="flat"
                      :loading="loading"
                      @click="saveCorrection"
                      >Salvar correção</v-btn
                    >
                  </v-col>
                </v-row>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </template>

    <template v-else-if="(isProfessor || isAdmin) && page === 'EstatisticaTurma'">
      <v-card prepend-icon="mdi-chart-box" title="Estatística da Turma">
        <v-card-text>
          <v-chip
            v-if="isAdmin"
            :color="isSimulatedMode ? 'warning' : 'success'"
            variant="flat"
            class="mb-3"
            :prepend-icon="isSimulatedMode ? 'mdi-file-excel' : 'mdi-database'"
          >
            {{
              isSimulatedMode
                ? "Modo simulado ativo (dados do arquivo Excel)"
                : "Exibindo dados reais do banco de dados"
            }}
          </v-chip>

          <div class="mt-4">
            <turma-estatisticas
              v-if="estatisticaSubPage !== 'Individual'"
              :students="spreadsheetStudents"
              :discipline-records="disciplineRecords"
              :simulated-mode="isSimulatedMode"
              @turma-selecionada="onTurmaSelecionada"
            />
            <turma-individual
              v-else
              :students="spreadsheetStudents"
              :discipline-records="disciplineRecords"
              :simulated-mode="isSimulatedMode"
              @turma-selecionada="onTurmaSelecionada"
            />
          </div>

          <v-alert
            v-if="spreadsheetError || disciplineError"
            type="error"
            variant="tonal"
            class="mt-3"
          >
            {{ spreadsheetError || disciplineError }}
          </v-alert>

          <template v-if="isAdmin">
            <v-divider class="my-6" />

            <p>
              Selecione uma planilha de notas para simular a visualização com
              dados de exemplo (modo simulado, disponível apenas para o
              administrador). Ao remover o arquivo, os gráficos voltam a
              exibir os dados reais do banco de dados.
            </p>

            <v-file-input
              v-model="spreadsheetFile"
              label="Arquivo Excel de notas"
              accept=".xls, .xlsx"
              prepend-icon="mdi-file-excel"
              show-size
              placeholder="Notas3T.xlsx"
              @update:model-value="handleSpreadsheetFile"
            />

            <v-file-input
              v-model="disciplineFile"
              label="Arquivo Excel de disciplinas"
              accept=".xls, .xlsx"
              prepend-icon="mdi-file-table"
              show-size
              placeholder="T3_Disciplinas.xls"
              class="mt-4"
              @update:model-value="handleDisciplineSpreadsheetFile"
            />

            <div v-if="spreadsheetName || disciplineName" class="mt-4">
              <p v-if="spreadsheetName">
                <strong>Arquivo de notas:</strong> {{ spreadsheetName }}
              </p>
              <p v-if="disciplineName">
                <strong>Arquivo de disciplinas:</strong> {{ disciplineName }}
              </p>
              <v-btn color="secondary" variant="text" @click="clearSpreadsheet"
                >Remover arquivo de notas</v-btn
              >
              <v-btn
                color="secondary"
                variant="text"
                class="ms-2"
                @click="clearDisciplineSpreadsheet"
              >
                Remover arquivo de disciplinas
              </v-btn>
            </div>

            <v-alert type="info" variant="tonal" class="mt-4">
              Modo simulado (somente administrador): o arquivo é processado
              apenas no navegador e não altera o banco de dados.
            </v-alert>
          </template>

        </v-card-text>
      </v-card>
    </template>

    <template v-else-if="isAluno && page === 'Aluno' && estatisticaSubPage === 'Dashboard'">
      <aluno-dashboard
        :student-name="storeUserName"
        :student-username="storeUserUsername"
        :discipline-records="disciplineRecords"
      />
    </template>

    <template v-else-if="isAluno && page === 'Aluno'">
      <v-card prepend-icon="mdi-pencil" title="Responder avaliação discursiva">
        <v-card-text>
          <v-text-field v-model="studentCode" label="Código da avaliação" />
          <v-btn color="primary" class="mb-4" @click="loadStudentEvaluation"
            >Abrir prova</v-btn
          >
          <template v-if="studentEvaluation">
            <v-divider class="my-4" />
            <p>
              <strong>{{ studentEvaluation.titulo }}</strong>
            </p>
            <p>
              Professor: {{ studentEvaluation.professor_nome }} | Turma:
              {{ studentEvaluation.turma }}
            </p>
            <v-text-field v-model="studentName" label="Nome do aluno" />
            <v-text-field v-model="studentEmail" label="E-mail do aluno" />
            <v-alert class="my-4" type="info" variant="tonal">
              ANTES DE INICIAR A PROVA LEIA ATENTAMENTE AS INSTRUÇÕES ABAIXO.
              Leia atentamente os textos contidos neste caderno e responda as
              questões propostas. A interpretação dos enunciados faz parte da
              avaliação.
            </v-alert>
            <div
              v-for="(questao, index) in studentEvaluation.questoes"
              :key="index"
              class="mb-5"
            >
              <h3>Questão {{ index + 1 }}</h3>
              <p style="white-space: pre-wrap">{{ questao.enunciado }}</p>
              <img
                v-if="questao.image_data_url"
                :src="questao.image_data_url"
                class="discursive-question-image my-3"
                alt="Figura da questão"
              />
              <p class="text-caption text-medium-emphasis mb-2">
                Linhas para resposta: {{ questionLines(questao) }}
              </p>
              <v-textarea
                v-model="studentAnswers[index].resposta"
                label="Resposta"
                :rows="questionLines(questao)"
                no-resize
              />
            </div>
            <v-btn
              color="primary"
              variant="flat"
              :loading="loading"
              @click="submitStudentAnswers"
              >Enviar respostas</v-btn
            >
          </template>
        </v-card-text>
      </v-card>
    </template>

    <template v-else-if="isAluno && page === 'ConsultaProva'">
      <v-card prepend-icon="mdi-clipboard-check" title="Consulta prova">
        <v-card-text>
          <v-row dense class="align-center mb-2">
            <v-col cols="12" md="8">
              <span class="text-caption text-medium-emphasis"
                >Aluno logado: {{ storeUserName }} | ID {{ storeUserId }}</span
              >
            </v-col>
            <v-col cols="12" md="4" class="text-md-right">
              <v-btn
                size="small"
                color="primary"
                variant="outlined"
                :loading="loading"
                @click="loadMyAnswers"
                >Atualizar</v-btn
              >
            </v-col>
          </v-row>
          <v-alert
            v-if="!loading && !myAnswers.length"
            type="info"
            variant="tonal"
            class="mb-4"
          >
            Nenhuma prova corrigida disponível no momento. A prova aparecerá
            aqui após a correção do professor.
          </v-alert>
          <v-list v-if="myAnswers.length" lines="three" class="discursive-list">
            <v-list-item v-for="answer in myAnswers" :key="answer.id">
              <template #title>
                <strong>{{ answer.avaliacao_titulo }}</strong>
              </template>
              <template #subtitle>
                <div class="d-flex flex-wrap ga-2 mt-2">
                  <v-chip size="small" color="primary" variant="tonal"
                    >Código: {{ answer.avaliacao_codigo }}</v-chip
                  >
                  <v-chip size="small" variant="tonal"
                    >Professor: {{ answer.professor_nome }}</v-chip
                  >
                  <v-chip size="small" color="success" variant="tonal">{{
                    answer.status
                  }}</v-chip>
                  <v-chip
                    v-if="
                      answer.nota_total !== null &&
                      answer.nota_total !== undefined
                    "
                    size="small"
                    color="success"
                    variant="tonal"
                    >Nota: {{ formatGrade(answer.nota_total) }}</v-chip
                  >
                </div>
                <div class="text-caption mt-1">
                  Corrigida em: {{ formatDate(answer.updated_at) }}
                </div>
              </template>
              <template #append>
                <v-btn
                  size="small"
                  color="primary"
                  variant="flat"
                  @click="selectAnswerItem(answer)"
                  >Abrir</v-btn
                >
              </template>
            </v-list-item>
          </v-list>
          <div v-if="selectedAnswer" class="mt-4">
            <h3>{{ selectedAnswer.avaliacao_titulo }}</h3>
            <v-row dense class="mb-2">
              <v-col cols="12" md="6"
                ><strong>Código:</strong>
                {{ selectedAnswer.avaliacao_codigo }}</v-col
              >
              <v-col cols="12" md="6"
                ><strong>Enviada em:</strong>
                {{ formatDate(selectedAnswer.datetime) }}</v-col
              >
              <v-col cols="12" md="6"
                ><strong>Professor:</strong>
                {{ selectedAnswer.professor_nome }}</v-col
              >
              <v-col cols="12" md="6"
                ><strong>Status:</strong> {{ selectedAnswer.status }}</v-col
              >
              <v-col
                v-if="
                  selectedAnswer.nota_total !== null &&
                  selectedAnswer.nota_total !== undefined
                "
                cols="12"
                md="6"
                ><strong>Nota total:</strong>
                {{ formatGrade(selectedAnswer.nota_total) }}</v-col
              >
            </v-row>
            <v-expansion-panels>
              <v-expansion-panel
                v-for="(questao, index) in selectedAnswer.questoes"
                :key="index"
              >
                <v-expansion-panel-title
                  >Questão {{ index + 1 }}</v-expansion-panel-title
                >
                <v-expansion-panel-text>
                  <p><strong>Enunciado</strong></p>
                  <p style="white-space: pre-wrap">{{ questao.enunciado }}</p>
                  <img
                    v-if="questao.image_data_url"
                    :src="questao.image_data_url"
                    class="discursive-question-image my-3"
                    alt="Figura da questão"
                  />
                  <p><strong>Sua resposta</strong></p>
                  <p style="white-space: pre-wrap">{{ answerText(index) }}</p>
                  <p v-if="selectedAnswer.notas && selectedAnswer.notas[index]">
                    <strong>Nota:</strong>
                    {{ selectedAnswer.notas[index].nota }} /
                    {{ questionValue(questao) }}
                  </p>
                </v-expansion-panel-text>
              </v-expansion-panel>
            </v-expansion-panels>
          </div>
        </v-card-text>
      </v-card>
    </template>
  </v-container>
</template>

<script>
import store from "@/store";
import AvaliacaoDiscursivaDataService from "@/assets/js/services/AvaliacaoDiscursivaDataService";
import DesempenhoDataService from "@/assets/js/services/DesempenhoDataService";
import UserDataService from "@/assets/js/services/UserDataService";
import TurmaEstatisticas from "@/components/TurmaEstatisticas.vue";
import TurmaIndividual from "@/components/TurmaIndividual.vue";
import AlunoDashboard from "@/components/AlunoDashboard.vue";
import * as XLSX from "xlsx";

const taxonomyLines = { Alta: 14, Média: 8, Baixa: 2 };
const defaultTaxonomy = (index) =>
  ["Alta", "Média", "Média", "Baixa", "Baixa", "Baixa"][index];

const defaultQuestion = (index) => {
  const taxonomia = defaultTaxonomy(index);
  return {
    valor: "",
    area: "",
    taxonomia,
    linhas: taxonomyLines[taxonomia],
    enunciado: "",
    gabarito: "",
    referencia: "",
    image_filename: "",
    image_data_url: "",
  };
};

export default {
  name: "AvaliacaoDiscursiva",
  components: { TurmaEstatisticas, TurmaIndividual, AlunoDashboard },
  data: () => ({
    alert: false,
    alertText: "",
    alertType: "success",
    loading: false,
    evaluations: [],
    answers: [],
    myAnswers: [],
    selectedEvaluation: null,
    selectedAnswer: null,
    spreadsheetFile: null,
    spreadsheetName: "",
    spreadsheetError: "",
    spreadsheetStudents: [],
    disciplineFile: null,
    disciplineName: "",
    disciplineError: "",
    disciplineRecords: [],
    professores: [],
    selectedProfessorId: null,
    selectedTurmaCodigo: null,
    loadingProfessores: false,
    turmas: [],
    loadingTurmas: false,
    adminStatusFilter: "Todos",
    evaluationSearch: "",
    protocolQuery: "",
    studentCode: "",
    studentEvaluation: null,
    studentName: store.state.userName || "",
    studentEmail: "",
    studentAnswers: [],
    statusOptions: [
      { value: "RECEBIDA", label: "Recebida" },
      { value: "EM_REVISAO", label: "Em revisão" },
      { value: "APROVADA", label: "Aprovada" },
      { value: "CORRECAO_SOLICITADA", label: "Correção solicitada" },
      { value: "ENVIADA_AO_ALUNO", label: "Enviada ao aluno" },
    ],
    adminStatusOptions: [
      { value: "Todos", label: "Todos" },
      { value: "RECEBIDA", label: "Recebida" },
      { value: "EM_REVISAO", label: "Em revisão" },
      { value: "APROVADA", label: "Aprovada" },
      { value: "CORRECAO_SOLICITADA", label: "Correção solicitada" },
      { value: "ENVIADA_AO_ALUNO", label: "Enviada ao aluno" },
    ],
    evaluationHeaders: [
      { title: "Título", key: "titulo" },
      { title: "Código", key: "access_code" },
      { title: "Turma", key: "turma" },
      { title: "Status", key: "status" },
      { title: "Respostas", key: "respostas_count" },
      { title: "Criada em", key: "datetime" },
      { title: "", key: "actions", sortable: false },
    ],
    answerHeaders: [
      { title: "Aluno", key: "nome_aluno" },
      { title: "RA", key: "aluno_ra" },
      { title: "E-mail", key: "email_aluno" },
      { title: "Código", key: "avaliacao_codigo" },
      { title: "Status", key: "status" },
      { title: "Nota total", key: "nota_total" },
      { title: "Enviada em", key: "datetime" },
      { title: "", key: "actions", sortable: false },
    ],
    myAnswerHeaders: [
      { title: "Avaliação", key: "avaliacao_titulo" },
      { title: "Código", key: "avaliacao_codigo" },
      { title: "Professor", key: "professor_nome" },
      { title: "Status", key: "status" },
      { title: "Nota total", key: "nota_total" },
      { title: "Enviada em", key: "datetime" },
      { title: "", key: "actions", sortable: false },
    ],
    form: {
      titulo: "",
      area_conhecimento: "",
      avaliacao: "A1",
      bimestre: "1B",
      turma: "",
      periodo_letivo: "2026-1",
      data_avaliacao: "",
      valor_total: "8,0",
      questoes: [],
    },
  }),
  computed: {
    page() {
      return this.$route.params.dPage || "Professor";
    },
    estatisticaSubPage() {
      return this.$route.params.subPage || "Geral";
    },
    isProfessor() {
      return (
        store.state.userType === "professor" ||
        store.state.userType === "coordenador" ||
        store.state.userType === "admin"
      );
    },
    isCoordenador() {
      return (
        store.state.userType === "coordenador" ||
        ["admin", "administrador_sistema"].includes(store.state.userType)
      );
    },
    isAdmin() {
      return ["admin", "administrador_sistema"].includes(store.state.userType);
    },
    isSimulatedMode() {
      return Boolean(this.spreadsheetFile || this.disciplineFile);
    },
    isAluno() {
      return store.state.userType === "aluno";
    },
    storeUserName() {
      return store.state.userName || "";
    },
    storeUserUsername() {
      return store.state.userUsername || "";
    },
    storeUserId() {
      return store.state.userId || "";
    },
    filteredEvaluations() {
      const termo = String(this.evaluationSearch || "")
        .trim()
        .toLowerCase();
      return (this.evaluations || []).filter((item) => {
        const statusOk =
          this.adminStatusFilter === "Todos" ||
          item.status === this.adminStatusFilter;
        const textOk =
          !termo ||
          [
            item.titulo,
            item.access_code,
            item.turma,
            item.id,
            item.professor_nome,
          ].some((value) =>
            String(value || "")
              .toLowerCase()
              .includes(termo)
          );
        return statusOk && textOk;
      });
    },
    selectedEvaluationAnswers() {
      if (!this.selectedEvaluation) return [];
      const answers = this.answers || [];
      const selectedId = String(this.selectedEvaluation.id);
      const matching = answers.filter((item) => {
        const avaliacaoId =
          item.avaliacao ||
          item.avaliacao_id ||
          item.avaliacaoId ||
          item.avaliacao?.id;
        return String(avaliacaoId || "") === selectedId;
      });
      return matching.length ? matching : answers;
    },
    visibleAnswers() {
      return this.selectedEvaluation
        ? this.selectedEvaluationAnswers
        : this.answers || [];
    },
  },
  mounted() {
    this.resetForm();
    this.loadTurmas();
    this.loadProfessores();
    this.loadDesempenhoDashboard();
    this.refreshPage();
  },
  watch: {
    "$route.params.dPage"() {
      this.refreshPage();
    },
  },
  methods: {
    notify(type, text) {
      this.alertType = type;
      this.alertText = text;
      this.alert = true;
    },
    rowValue(item) {
      return item?.raw || item?.value || item;
    },
    formatDate(value) {
      if (!value) return "";
      const date = new Date(value);
      return Number.isNaN(date.getTime())
        ? String(value)
        : date.toLocaleString("pt-BR");
    },
    statusColor(value) {
      return (
        {
          RECEBIDA: "grey",
          EM_REVISAO: "info",
          APROVADA: "success",
          CORRECAO_SOLICITADA: "warning",
          ENVIADA_AO_ALUNO: "primary",
        }[value] || "grey"
      );
    },
    fileFromEvent(event) {
      if (Array.isArray(event)) return event[0];
      if (event?.target?.files?.length) return event.target.files[0];
      return event || null;
    },
    handleQuestionImage(event, index) {
      const file = this.fileFromEvent(event);
      if (!file) return;
      if (!["image/png", "image/jpeg"].includes(file.type)) {
        this.notify("error", "Envie uma imagem PNG ou JPG/JPEG.");
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        this.form.questoes[index].image_filename = file.name;
        this.form.questoes[index].image_data_url = reader.result;
      };
      reader.readAsDataURL(file);
    },
    removeQuestionImage(index) {
      this.form.questoes[index].image_filename = "";
      this.form.questoes[index].image_data_url = "";
    },
    questionLines(questao) {
      const linhas = Number(
        questao?.linhas || taxonomyLines[questao?.taxonomia] || 2
      );
      return Math.max(1, Math.min(30, linhas));
    },
    applyTaxonomyLines(index) {
      const questao = this.form.questoes[index];
      questao.linhas =
        taxonomyLines[questao.taxonomia] || this.questionLines(questao);
    },
    adjustQuestionLines(index, amount) {
      const questao = this.form.questoes[index];
      questao.linhas = this.questionLines(questao) + amount;
      if (questao.linhas < 1) questao.linhas = 1;
    },
    normalizeQuestionLines() {
      this.form.questoes = this.form.questoes.map((questao) => ({
        ...questao,
        linhas: this.questionLines(questao),
      }));
    },
    statusLabel(value) {
      return (
        (this.statusOptions.find((item) => item.value === value) || {}).label ||
        value
      );
    },
    resetForm() {
      this.form.questoes = Array.from({ length: 6 }, (_, index) =>
        defaultQuestion(index)
      );
    },
    async loadTurmas() {
      this.loadingTurmas = true;
      try {
        const response = await UserDataService.listTurmas();
        this.turmas = (response.data || [])
          .map((turma) => ({
            ...turma,
            codigo: String(turma.codigo || "").trim(),
            display: turma.nome
              ? `${turma.codigo} - ${turma.nome}`
              : turma.codigo,
          }))
          .filter((turma) => turma.codigo);

        if (this.form.turma && !this.turmas.some((turma) => turma.codigo === this.form.turma)) {
          this.form.turma = "";
        }
      } catch (_error) {
        this.turmas = [];
        this.notify("error", "Não foi possível carregar as turmas cadastradas.");
      } finally {
        this.loadingTurmas = false;
      }
    },
    handleTurmaChange(codigo) {
      this.form.turma = codigo || "";
    },
    async loadProfessores() {
      if (!this.isCoordenador) return;
      this.loadingProfessores = true;
      try {
        const response = await UserDataService.listProfessores();
        this.professores = Array.isArray(response.data) ? response.data : [];
      } catch (_error) {
        this.professores = [];
        this.notify("error", "Não foi possível carregar os professores.");
      } finally {
        this.loadingProfessores = false;
      }
    },
    async refreshPage() {
      if (
        (this.isProfessor && this.page === "EstatisticaTurma") ||
        (this.isAluno && this.page === "Aluno")
      ) {
        await this.loadDesempenhoDashboard();
      }
      if (
        this.isProfessor &&
        ["Admin", "ConsultarProtocolo"].includes(this.page)
      ) {
        await this.loadProfessorData();
      }
      if (this.isAluno && this.page === "Aluno") {
        this.loadStoredDisciplineRecords();
      }
      if (this.isAluno && this.page === "ConsultaProva") {
        await this.loadMyAnswers();
      }
    },
    loadStoredDisciplineRecords() {
      if (this.disciplineRecords.length) return;
      const raw = localStorage.getItem("disciplineRecords");
      if (!raw) return;
      try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed) && parsed.length) {
          this.disciplineRecords = parsed;
          this.disciplineError = "";
        }
      } catch (_error) {
        localStorage.removeItem("disciplineRecords");
      }
    },
    async onTurmaSelecionada(codigo) {
      this.selectedTurmaCodigo = codigo;
      await this.loadDesempenhoDashboard();
    },
    async loadDesempenhoDashboard() {
      try {
        const params = {};
        if (this.isCoordenador && this.selectedProfessorId) {
          params.professor_id = this.selectedProfessorId;
        }
        if (this.selectedTurmaCodigo) {
          params.turma_codigo = this.selectedTurmaCodigo;
        }
        const response = await DesempenhoDataService.dashboard(params);
        const data = response.data || {};
        if (
          data.scope &&
          data.scope.professor_id &&
          data.scope.professor_mappings_count === 0
        ) {
          this.spreadsheetStudents = [];
          this.disciplineRecords = [];
          this.spreadsheetError =
            "Nenhuma disciplina/turma foi vinculada a este professor.";
          this.disciplineError =
            "Configure os vínculos do professor no cadastro administrativo.";
          return;
        }
        if (Array.isArray(data.students)) {
          // Atualiza sempre, mesmo com array vazio — uma turma sem dados
          // cadastrados tem que limpar a tela, nao manter o que a turma
          // selecionada antes deixou la.
          this.spreadsheetStudents = data.students;
          this.spreadsheetName = data.students.length
            ? data.imports?.tp?.arquivo ||
              data.imports?.disciplinas?.arquivo ||
              "Banco de dados"
            : "";
          this.spreadsheetError = "";
        }
        if (Array.isArray(data.disciplineRecords)) {
          this.disciplineRecords = data.disciplineRecords.map((rec) => ({
            ...rec,
            a3a1: rec.a3a1 ?? rec.a3_de_a1 ?? null,
            a3a2: rec.a3a2 ?? rec.a3_de_a2 ?? null,
          }));
          this.disciplineName = data.disciplineRecords.length
            ? data.imports?.disciplinas?.arquivo || "Banco de dados"
            : "";
          this.disciplineError = "";
        }
      } catch (_error) {
        this.loadStoredDisciplineRecords();
      }
    },
    async loadProfessorData() {
      this.loading = true;
      try {
        const [evals, answers] = await Promise.all([
          AvaliacaoDiscursivaDataService.list(),
          AvaliacaoDiscursivaDataService.answers(),
        ]);
        this.evaluations = Array.isArray(evals.data) ? evals.data : [];
        this.answers = Array.isArray(answers.data) ? answers.data : [];
        if (this.selectedEvaluation) {
          this.selectedEvaluation =
            this.evaluations.find(
              (item) => String(item.id) === String(this.selectedEvaluation.id)
            ) || this.selectedEvaluation;
        }
      } catch (error) {
        this.notify(
          "error",
          error?.response?.data?.error || "Erro ao carregar dados."
        );
      }
      this.loading = false;
    },
    async createEvaluation() {
      this.loading = true;
      try {
        if (!this.form.turma) {
          this.notify("warning", "Selecione uma turma antes de gerar a avaliação.");
          this.loading = false;
          return;
        }
        this.normalizeQuestionLines();
        const response = await AvaliacaoDiscursivaDataService.create(this.form);
        this.notify(
          "success",
          `Avaliação criada. Código para alunos: ${response.data.access_code}`
        );
        await this.loadProfessorData();
      } catch (error) {
        this.notify(
          "error",
          error?.response?.data?.error || "Erro ao criar avaliação."
        );
      }
      this.loading = false;
    },
    selectEvaluation(_event, row) {
      this.selectEvaluationItem(row?.item);
    },
    selectEvaluationItem(item) {
      this.selectedEvaluation = { ...this.rowValue(item) };
      this.selectedAnswer = null;
    },
    selectAnswer(_event, row) {
      this.selectAnswerItem(row?.item);
    },
    selectAnswerItem(item) {
      const answer = { ...this.rowValue(item) };
      answer.notas = this.buildGradeItems(answer);
      this.selectedAnswer = answer;
    },
    async updateEvaluation() {
      try {
        const response = await AvaliacaoDiscursivaDataService.update({
          id: this.selectedEvaluation.id,
          status: this.selectedEvaluation.status,
          observacao_admin: this.selectedEvaluation.observacao_admin,
        });
        this.selectedEvaluation = response.data;
        this.notify("success", "Status atualizado.");
        await this.loadProfessorData();
        this.selectedEvaluation =
          this.evaluations.find((item) => item.id === response.data.id) ||
          response.data;
      } catch (error) {
        this.notify(
          "error",
          error?.response?.data?.error || "Erro ao atualizar status."
        );
      }
    },
    async findProtocol() {
      const termo = String(this.protocolQuery || "").trim();
      if (!termo) {
        this.notify("error", "Informe o código ou protocolo.");
        return;
      }
      this.loading = true;
      try {
        const evals = await AvaliacaoDiscursivaDataService.list();
        this.evaluations = Array.isArray(evals.data) ? evals.data : [];
        const item = this.evaluations.find(
          (ev) =>
            String(ev.access_code || "").toUpperCase() ===
              termo.toUpperCase() || String(ev.id) === termo
        );
        this.selectedEvaluation = item || null;
        this.selectedAnswer = null;
        if (!item) {
          this.answers = [];
          this.notify(
            "error",
            "Protocolo não encontrado entre suas avaliações."
          );
          return;
        }
        const answers = await AvaliacaoDiscursivaDataService.answers(item.id);
        this.answers = Array.isArray(answers.data) ? answers.data : [];
      } catch (error) {
        this.notify(
          "error",
          error?.response?.data?.error || "Erro ao consultar protocolo."
        );
      } finally {
        this.loading = false;
      }
    },
    async loadStudentEvaluation() {
      try {
        const response = await AvaliacaoDiscursivaDataService.alunoReadByCode(
          this.studentCode
        );
        this.studentEvaluation = response.data;
        this.studentAnswers = this.studentEvaluation.questoes.map(
          (_, index) => ({ questao: index + 1, resposta: "" })
        );
      } catch (error) {
        this.notify(
          "error",
          error?.response?.data?.error || "Erro ao abrir prova."
        );
      }
    },
    async submitStudentAnswers() {
      this.loading = true;
      try {
        await AvaliacaoDiscursivaDataService.alunoSubmit({
          access_code: this.studentCode,
          nome_aluno: this.studentName,
          email_aluno: this.studentEmail,
          aluno_ra: store.state.userRa || "",
          respostas: this.studentAnswers,
        });
        this.notify("success", "Respostas enviadas com sucesso.");
        this.studentEvaluation = null;
        await this.loadMyAnswers();
      } catch (error) {
        this.notify(
          "error",
          error?.response?.data?.error || "Erro ao enviar respostas."
        );
      }
      this.loading = false;
    },
    async loadMyAnswers() {
      this.loading = true;
      try {
        const response = await AvaliacaoDiscursivaDataService.alunoMyAnswers();
        this.myAnswers = Array.isArray(response.data) ? response.data : [];
      } catch (error) {
        this.notify("error", "Erro ao carregar provas.");
      }
      this.loading = false;
    },
    handleSpreadsheetFile(event) {
      const file = this.fileFromEvent(event);
      if (!file) {
        this.spreadsheetFile = null;
        this.spreadsheetName = "";
        this.spreadsheetError = "";
        return;
      }
      const allowedTypes = [
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "application/vnd.ms-excel",
      ];
      if (
        !allowedTypes.includes(file.type) &&
        !/\.(xls|xlsx)$/i.test(file.name)
      ) {
        this.spreadsheetError =
          "Formato inválido. Envie um arquivo .xls ou .xlsx.";
        this.spreadsheetFile = null;
        this.spreadsheetName = "";
        return;
      }
      this.spreadsheetError = "";
      this.spreadsheetFile = file;
      this.spreadsheetName = file.name;
      this.parseSpreadsheetFile(file);
    },
    handleDisciplineSpreadsheetFile(event) {
      const file = this.fileFromEvent(event);
      if (!file) {
        this.disciplineFile = null;
        this.disciplineName = "";
        this.disciplineError = "";
        return;
      }
      const allowedTypes = [
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "application/vnd.ms-excel",
      ];
      if (
        !allowedTypes.includes(file.type) &&
        !/\.(xls|xlsx)$/i.test(file.name)
      ) {
        this.disciplineError =
          "Formato inválido. Envie um arquivo .xls ou .xlsx.";
        this.disciplineFile = null;
        this.disciplineName = "";
        return;
      }
      this.disciplineError = "";
      this.disciplineFile = file;
      this.disciplineName = file.name;
      this.parseDisciplineSpreadsheetFile(file);
    },
    parseSpreadsheetFile(file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          const arrayBuffer = event.target.result;
          const workbook = XLSX.read(arrayBuffer, {
            type: "array",
            cellDates: true,
            raw: false,
          });

          const normalizeHeader = (value) =>
            String(value || "")
              .trim()
              .toLowerCase();

          const isNameHeader = (cell) =>
            [
              "nome",
              "nomes",
              "nome do aluno",
              "nome completo",
              "aluno",
              "alunos",
            ].includes(cell);

          const isLastNameHeader = (cell) =>
            [
              "sobrenome",
              "último nome",
              "ultimo nome",
              "last name",
              "surname",
            ].includes(cell);

          const parseSheetRows = (sheet) =>
            XLSX.utils.sheet_to_json(sheet, {
              header: 1,
              raw: false,
              defval: "",
            });

          const findHeader = (rows) => {
            return rows.findIndex((row) => {
              const normalized = Array.isArray(row)
                ? row.map(normalizeHeader)
                : [];
              return (
                normalized.some(isNameHeader) ||
                (normalized.some((cell) => cell === "nome") &&
                  normalized.some(isLastNameHeader))
              );
            });
          };

          const buildStudentsFromRows = (rows, headerIndex) => {
            const headerRow = Array.isArray(rows[headerIndex])
              ? rows[headerIndex]
              : [];
            const header = headerRow.map(normalizeHeader);
            const namesIndex = header.findIndex(isNameHeader);
            const firstNameIndex = header.findIndex((cell) =>
              ["nome", "primeiro nome", "first name"].includes(cell)
            );
            const lastNameIndex = header.findIndex(isLastNameHeader);
            const nameColumns = header
              .map((cell, index) => ({ cell, index }))
              .filter(
                ({ cell }) => isNameHeader(cell) || isLastNameHeader(cell)
              )
              .map(({ index }) => index);
            const isStudentIdHeader = (cell) =>
              [
                "numero",
                "número",
                "numero do aluno",
                "número do aluno",
                "matricula",
                "matrícula",
                "rm",
                "id",
                "codigo",
                "código",
              ].includes(cell);
            const numericIndexes = header
              .map((cell, index) => ({ cell, index }))
              .filter(
                ({ cell, index }) =>
                  !nameColumns.includes(index) && !isStudentIdHeader(cell)
              )
              .map(({ index }) => index);

            const parseGradeCell = (cell) => {
              const raw = String(cell || "").trim();
              if (raw === "") return null;
              const parsed = this.parseGradeValue(raw);
              return Number.isFinite(parsed) ? parsed : null;
            };

            return rows
              .slice(headerIndex + 1)
              .filter((row) =>
                Array.isArray(row)
                  ? row.some((cell) => String(cell || "").trim())
                  : false
              )
              .map((row, rowIndex) => {
                const values = Array.isArray(row) ? row : [];
                const rawValues = values.map((cell) =>
                  String(cell || "").trim()
                );
                let name = "";

                if (namesIndex >= 0) {
                  name = rawValues[namesIndex] || "";
                } else if (firstNameIndex >= 0 && lastNameIndex >= 0) {
                  const first = rawValues[firstNameIndex];
                  const last = rawValues[lastNameIndex];
                  name = [first, last].filter(Boolean).join(" ");
                } else if (nameColumns.length) {
                  name = nameColumns
                    .map((column) => rawValues[column])
                    .filter(Boolean)
                    .join(" ");
                } else {
                  name = rawValues.find(Boolean) || "";
                }

                const numericValues = numericIndexes
                  .map((index) => ({
                    header: headerRow[index] || `Coluna ${index + 1}`,
                    value: parseGradeCell(values[index]),
                  }))
                  .filter((item) => item.value !== null);

                const seriesLabels = numericValues.map((item) =>
                  String(item.header)
                );
                const valuesOnly = numericValues.map((item) => item.value);

                const tpIndexes = numericValues
                  .map((item, idx) => ({
                    idx,
                    header: normalizeHeader(item.header),
                  }))
                  .filter((item) => /tp|trabalho|t\.p\b/i.test(item.header))
                  .map((item) => item.idx);

                const medIndexes = numericValues
                  .map((item, idx) => ({
                    idx,
                    header: normalizeHeader(item.header),
                  }))
                  .filter(
                    (item) =>
                      /med|m[eé]dia|media|nota|avalia/i.test(item.header) &&
                      !/tp|trabalho|t\.p\b/i.test(item.header)
                  )
                  .map((item) => item.idx);

                const medSeries = medIndexes.length
                  ? medIndexes.map((index) => valuesOnly[index])
                  : valuesOnly;
                const tpSeries = tpIndexes.length
                  ? tpIndexes.map((index) => valuesOnly[index])
                  : [];

                return {
                  id: rowIndex + 1,
                  name,
                  values: valuesOnly,
                  seriesLabels,
                  medSeries,
                  tpSeries,
                };
              })
              .filter((student) => student.name);
          };

          const normalizeStudentName = (name) =>
            String(name || "")
              .trim()
              .toLowerCase();

          const studentMap = new Map();

          for (const sheetName of workbook.SheetNames) {
            const sheet = workbook.Sheets[sheetName];
            const rows = parseSheetRows(sheet);
            const headerIndex = findHeader(rows);
            if (headerIndex >= 0) {
              const sheetStudents = buildStudentsFromRows(rows, headerIndex);
              for (const student of sheetStudents) {
                const nameKey = normalizeStudentName(student.name);
                if (!nameKey) continue;

                if (studentMap.has(nameKey)) {
                  const existing = studentMap.get(nameKey);
                  existing.seriesLabels = [
                    ...existing.seriesLabels,
                    ...student.seriesLabels,
                  ];
                  existing.values = [...existing.values, ...student.values];

                  if (student.medSeries?.length) {
                    existing.medSeries = [
                      ...existing.medSeries,
                      ...student.medSeries,
                    ];
                  }
                  if (student.tpSeries?.length) {
                    existing.tpSeries = [
                      ...existing.tpSeries,
                      ...student.tpSeries,
                    ];
                  }
                } else {
                  studentMap.set(nameKey, { ...student });
                }
              }
            }
          }

          let students = Array.from(studentMap.values());

          if (!students.length) {
            const fallbackSheet = workbook.SheetNames[0];
            const rows = parseSheetRows(workbook.Sheets[fallbackSheet]);
            const headerIndex = findHeader(rows) >= 0 ? findHeader(rows) : 0;
            students = buildStudentsFromRows(rows, headerIndex);
          }

          if (!students.length) {
            throw new Error(
              "Não foi possível localizar nomes válidos na planilha. Verifique a coluna de nomes."
            );
          }

          this.spreadsheetStudents = students;
          this.spreadsheetError = "";
        } catch (error) {
          this.spreadsheetStudents = [];
          this.spreadsheetError =
            error?.message ||
            "Erro ao processar a planilha. Verifique o formato do arquivo.";
        }
      };
      reader.readAsArrayBuffer(file);
    },
    async clearSpreadsheet() {
      this.spreadsheetFile = null;
      this.spreadsheetName = "";
      this.spreadsheetError = "";
      this.spreadsheetStudents = [];
      localStorage.removeItem("spreadsheetStudents");
      await this.loadDesempenhoDashboard();
    },
    async clearDisciplineSpreadsheet() {
      this.disciplineFile = null;
      this.disciplineName = "";
      this.disciplineError = "";
      this.disciplineRecords = [];
      localStorage.removeItem("disciplineRecords");
      await this.loadDesempenhoDashboard();
    },
    parseDisciplineSpreadsheetFile(file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          const arrayBuffer = event.target.result;
          const workbook = XLSX.read(arrayBuffer, {
            type: "array",
            cellDates: true,
            raw: false,
          });

          const normalizeHeader = (value) =>
            String(value || "")
              .trim()
              .toLowerCase();

          const parseSheetRows = (sheet) =>
            XLSX.utils.sheet_to_json(sheet, {
              header: 1,
              raw: false,
              defval: "",
            });

          const findHeader = (rows) => {
            return rows.findIndex((row) => {
              const normalized = Array.isArray(row)
                ? row.map(normalizeHeader)
                : [];
              const hasPeriod = normalized.some((cell) =>
                ["periodo", "semestre", "period"].includes(cell)
              );
              const hasStudent = normalized.some((cell) =>
                ["estudante", "aluno", "nome", "alunos", "estudantes"].includes(
                  cell
                )
              );
              const hasDiscipline = normalized.some((cell) =>
                ["disciplina", "materia", "matéria", "subject"].includes(cell)
              );
              const hasA1 = normalized.some((cell) =>
                ["nota a1", "nota a1", "a1"].includes(cell)
              );
              const hasA2 = normalized.some((cell) =>
                ["nota a2", "nota a2", "a2"].includes(cell)
              );
              return (
                hasPeriod && hasStudent && hasDiscipline && (hasA1 || hasA2)
              );
            });
          };

          const normalizePeriod = (value) => {
            const raw = String(value || "")
              .trim()
              .toLowerCase();
            if (/1\s*º|1º|1\s*p|p1|primeiro|semestre 1|semestre01/.test(raw)) {
              return "1P";
            }
            if (/2\s*º|2º|2\s*p|p2|segundo|semestre 2|semestre02/.test(raw)) {
              return "2P";
            }
            if (/3\s*º|3º|3\s*p|p3|terceiro|semestre 3|semestre03/.test(raw)) {
              return "3P";
            }
            return raw.toUpperCase();
          };

          const parseGradeCell = (cell) => {
            const raw = String(cell || "").trim();
            if (raw === "") return null;
            const parsed = this.parseGradeValue(raw);
            return Number.isFinite(parsed) ? parsed : null;
          };

          const parseAbsenceCell = (cell, hasColumn) => {
            const raw = String(cell || "").trim();
            if (raw === "") return hasColumn ? 0 : null;
            const parsed = this.parseGradeValue(raw);
            return Number.isFinite(parsed) ? parsed : raw;
          };

          const findColumnIndex = (header, names) =>
            header.findIndex((cell) => names.includes(normalizeHeader(cell)));

          const findColumnIndexWithPatterns = (header, names, patterns) =>
            header.findIndex((cell) => {
              const normalized = normalizeHeader(cell);
              return (
                names.includes(normalized) ||
                patterns.some((pattern) => pattern.test(normalized))
              );
            });

          const buildDisciplineRecords = (rows, headerIndex) => {
            const header = Array.isArray(rows[headerIndex])
              ? rows[headerIndex]
              : [];
            const periodIndex = findColumnIndex(header, [
              "periodo",
              "semestre",
              "period",
            ]);
            const studentIndex = findColumnIndex(header, [
              "estudante",
              "aluno",
              "nome",
              "alunos",
              "estudantes",
            ]);
            const disciplineIndex = findColumnIndex(header, [
              "disciplina",
              "materia",
              "matéria",
              "subject",
            ]);
            const a1Index = findColumnIndex(header, ["nota a1", "a1"]);
            const a2Index = findColumnIndex(header, ["nota a2", "a2"]);
            const a3a1Index = findColumnIndex(header, [
              "a3 de a1",
              "a3a1",
              "a3 a1",
              "a3_de_a1",
            ]);
            const a3a2Index = findColumnIndex(header, [
              "a3 de a2",
              "a3a2",
              "a3 a2",
              "a3_de_a2",
            ]);
            const a1AbsenceIndex = findColumnIndexWithPatterns(
              header,
              [
                "faltas a1",
                "falta a1",
                "faltas a1",
                "a1 faltas",
                "a1 falta",
              ],
              [/(faltas|falta|ausencia|ausências|ausencias).*a1|a1.*(faltas|falta|ausencia|ausências|ausencias)/i]
            );
            const a2AbsenceIndex = findColumnIndexWithPatterns(
              header,
              [
                "faltas a2",
                "falta a2",
                "faltas a 2",
                "a2 faltas",
                "a2 falta",
                "a2 faltas",
              ],
              [
                /(faltas|falta|ausencia|ausências|ausencias).*a2|a2.*(faltas|falta|ausencia|ausências|ausencias)/i,
                /faltas.*2|2.*faltas/i,
              ]
            );
            const a1ComponentIndexes = header
              .map((cell, index) => ({ cell: normalizeHeader(cell), index }))
              .filter(
                ({ cell }) =>
                  /(?:teo|teoria|prat|prática|pbl|proj|trabalho).*a1|a1.*(?:teo|teoria|prat|prática|pbl|proj|trabalho)/i.test(
                    cell
                  ) &&
                  !/a3/.test(cell)
              )
              .map(({ index }) => index);
            const a2ComponentIndexes = header
              .map((cell, index) => ({ cell: normalizeHeader(cell), index }))
              .filter(
                ({ cell }) =>
                  /(?:teo|teoria|prat|prática|pbl|proj|trabalho).*a2|a2.*(?:teo|teoria|prat|prática|pbl|proj|trabalho)/i.test(
                    cell
                  ) &&
                  !/a3/.test(cell)
              )
              .map(({ index }) => index);

            return rows
              .slice(headerIndex + 1)
              .filter((row) =>
                Array.isArray(row)
                  ? row.some((cell) => String(cell || "").trim())
                  : false
              )
              .map((row, rowIndex) => {
                const values = Array.isArray(row) ? row : [];
                const period = normalizePeriod(values[periodIndex]);
                const studentName = String(values[studentIndex] || "").trim();
                const discipline = String(values[disciplineIndex] || "").trim();
                const a1Components = a1ComponentIndexes
                  .map((index) => ({
                    label: header[index] || `A1 comp ${index}`,
                    value: parseGradeCell(values[index]),
                  }))
                  .filter((item) => item.value !== null);
                const a2Components = a2ComponentIndexes
                  .map((index) => ({
                    label: header[index] || `A2 comp ${index}`,
                    value: parseGradeCell(values[index]),
                  }))
                  .filter((item) => item.value !== null);
                const rawA1 = a1Components.length
                  ? a1Components.reduce((sum, item) => sum + item.value, 0)
                  : parseGradeCell(values[a1Index]);
                const rawA2 = a2Components.length
                  ? a2Components.reduce((sum, item) => sum + item.value, 0)
                  : parseGradeCell(values[a2Index]);
                const recoveryA1 = parseGradeCell(values[a3a1Index]);
                const recoveryA2 = parseGradeCell(values[a3a2Index]);
                const a1Values = [rawA1, recoveryA1].filter(
                  (value) => value !== null
                );
                const a2Values = [rawA2, recoveryA2].filter(
                  (value) => value !== null
                );
                const effectiveA1 = a1Values.length
                  ? Math.max(...a1Values)
                  : null;
                const effectiveA2 = a2Values.length
                  ? Math.max(...a2Values)
                  : null;
                const a1Source =
                  effectiveA1 === null
                    ? null
                    : effectiveA1 === recoveryA1 && recoveryA1 !== rawA1
                    ? "A3 de A1"
                    : "A1";
                const a2Source =
                  effectiveA2 === null
                    ? null
                    : effectiveA2 === recoveryA2 && recoveryA2 !== rawA2
                    ? "A3 de A2"
                    : "A2";
                const finalAverage =
                  effectiveA1 !== null && effectiveA2 !== null
                    ? (effectiveA1 + effectiveA2) / 2
                    : effectiveA1 !== null
                    ? effectiveA1
                    : effectiveA2;
                const a1Absences = parseAbsenceCell(
                  values[a1AbsenceIndex],
                  a1AbsenceIndex >= 0
                );
                const a2Absences = parseAbsenceCell(
                  values[a2AbsenceIndex],
                  a2AbsenceIndex >= 0
                );

                return {
                  id: rowIndex + 1,
                  studentName,
                  period,
                  discipline,
                  a1: rawA1,
                  a1Source,
                  a3a1: recoveryA1,
                  a2: rawA2,
                  a2Source,
                  a3a2: recoveryA2,
                  componentsA1: a1Components,
                  componentsA2: a2Components,
                  a1Absences,
                  a2Absences,
                  average: finalAverage,
                };
              })
              .filter(
                (record) =>
                  record.studentName && record.discipline && record.period
              );
          };

          const disciplineRecords = [];
          for (const sheetName of workbook.SheetNames) {
            const sheet = workbook.Sheets[sheetName];
            const rows = parseSheetRows(sheet);
            const headerIndex = findHeader(rows);
            if (headerIndex >= 0) {
              disciplineRecords.push(
                ...buildDisciplineRecords(rows, headerIndex)
              );
            }
          }

          if (!disciplineRecords.length) {
            throw new Error(
              "Não foi possível localizar registros de disciplina válidos na planilha. Verifique os cabeçalhos."
            );
          }

          this.disciplineRecords = disciplineRecords;
          this.disciplineError = "";
          localStorage.setItem(
            "disciplineRecords",
            JSON.stringify(disciplineRecords)
          );
        } catch (error) {
          this.disciplineRecords = [];
          this.disciplineError =
            error?.message ||
            "Erro ao processar a planilha de disciplinas. Verifique o formato do arquivo.";
          localStorage.removeItem("disciplineRecords");
        }
      };
      reader.readAsArrayBuffer(file);
    },
    parseGradeValue(value) {
      if (value === null || value === undefined || value === "") return 0;
      const raw = String(value).trim();
      const normalized = raw.includes(",")
        ? raw.replace(/\./g, "").replace(",", ".")
        : raw;
      const number = Number(normalized);
      return Number.isFinite(number) ? number : 0;
    },
    formatGrade(value) {
      const number = this.parseGradeValue(value);
      return number.toLocaleString("pt-BR", {
        minimumFractionDigits: 1,
        maximumFractionDigits: 2,
      });
    },
    questionValue(questao) {
      return questao?.valor || "0";
    },
    buildGradeItems(answer) {
      const current = Array.isArray(answer?.notas) ? answer.notas : [];
      const questoes = answer?.questoes || [];
      return questoes.map((questao, index) => ({
        questao: index + 1,
        valor: this.questionValue(questao),
        nota: current[index]?.nota ?? "",
      }));
    },
    correctionTotalNumber(answer) {
      return (answer?.notas || []).reduce(
        (total, item) => total + this.parseGradeValue(item.nota),
        0
      );
    },
    correctionTotalLabel(answer) {
      return this.formatGrade(this.correctionTotalNumber(answer));
    },
    async saveCorrection() {
      if (!this.selectedAnswer) return;
      this.loading = true;
      try {
        const response = await AvaliacaoDiscursivaDataService.gradeAnswer({
          id: this.selectedAnswer.id,
          notas: this.selectedAnswer.notas,
          observacao_admin: this.selectedAnswer.observacao_admin || "",
        });
        const updated = response.data;
        updated.notas = this.buildGradeItems(updated);
        this.selectedAnswer = updated;
        this.answers = (this.answers || []).map((item) =>
          String(item.id) === String(updated.id) ? updated : item
        );
        this.notify(
          "success",
          "Correção salva. Status da prova do aluno: Corrigida."
        );
        if (this.selectedEvaluation) {
          const answers = await AvaliacaoDiscursivaDataService.answers(
            this.selectedEvaluation.id
          );
          this.answers = Array.isArray(answers.data)
            ? answers.data
            : this.answers;
          this.selectedAnswer = {
            ...updated,
            notas: this.buildGradeItems(updated),
          };
        }
      } catch (error) {
        this.notify(
          "error",
          error?.response?.data?.error || "Erro ao salvar correção."
        );
      } finally {
        this.loading = false;
      }
    },
    answerText(index) {
      const item = this.selectedAnswer?.respostas?.[index];
      return item?.resposta || "Sem resposta registrada.";
    },
  },
};
</script>

<style scoped>
.discursive-question-image {
  display: block;
  width: 100%;
  max-width: 100%;
  height: auto;
  object-fit: contain;
}

.discursive-list {
  max-height: 520px;
  overflow-y: auto;
}
</style>
